import { ChangeEvent, FC, ReactNode, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Tooltip,
  Typography
} from '@mui/material';

import { ConfirmationDialog } from 'components/shared';
import { TableHeaderRow, ZebraStripedTableRow } from 'components/shared/Table';
import { Icon } from '@sdb/planning-ui';

const TableHeader = styled(Box)`
  display: flex;
  margin-bottom: 24px;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
  }
`;

type Cell =
  | string
  | number
  | {
      value: string | number;
      url?: string;
    };

type Row = {
  id: string | number;
  cells: Cell[];
};

export type TableData = {
  columns: string[];
  rows: Row[];
};

interface RowWithCheckbox extends Row {
  checked: boolean;
}

interface CommonProps {
  data: TableData;
  onAddClick?: () => void;
  onDeleteClick?: (id: string | number) => void;
  onEditClick?: (id: string | number) => void;
  onCopyClick?: (id: string | number) => void;
  addRowDialog?: ReactNode;
  disableActions?: boolean;
}

type CheckboxesProps =
  | {
      checkboxes?: true;
      handleDeleteSelection: (rows: RowWithCheckbox[]) => void;
      confirmationTitle: string;
      confirmationBody: string;
      title?: never;
    }
  | {
      checkboxes?: false;
      handleDeleteSelection?: never;
      confirmationTitle?: never;
      confirmationBody?: never;
      title?: string;
    };

type TableProps = CommonProps & CheckboxesProps;

const CustomTable: FC<TableProps> = ({
  title,
  data,
  onAddClick,
  onDeleteClick,
  onEditClick,
  onCopyClick,
  addRowDialog,
  checkboxes,
  handleDeleteSelection,
  confirmationTitle,
  confirmationBody,
  disableActions
}) => {
  const { columns, rows: _rows } = data;
  const { t } = useTranslation('common');
  const hasActions = disableActions
    ? false
    : Boolean(onDeleteClick || onEditClick || onCopyClick);

  const [rows, setRows] = useState<RowWithCheckbox[] | undefined>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    const newRows = _rows.map((row) => ({ ...row, checked: false }));
    setRows(newRows);
  }, [_rows]);

  const onCheckAll = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setRows((prev) => prev?.map((row) => ({ ...row, checked: true })));
    } else {
      setRows((prev) => prev?.map((row) => ({ ...row, checked: false })));
    }
  };

  const onCheckboxChange = (id: string | number) => {
    const newRows = rows?.map((row) => {
      if (row.id === id) {
        return {
          ...row,
          checked: !row.checked
        };
      }

      return row;
    });
    setRows(newRows);
  };

  const onDeleteConfirmation = (isConfirmed: boolean) => {
    if (isConfirmed && handleDeleteSelection) {
      handleDeleteSelection(rows?.filter((row) => row.checked) ?? []);
    }
    setShowDeleteConfirmation(false);
  };

  return (
    <Box>
      {!disableActions && (
        <TableHeader>
          {checkboxes && !disableActions && (
            <Button
              variant="contained"
              onClick={() => setShowDeleteConfirmation(true)}
            >
              {t('deleteSelection')}
            </Button>
          )}
          {title && !checkboxes && (
            <Typography variant="h6">{title}</Typography>
          )}
          {onAddClick && !disableActions && (
            <Button
              variant="contained"
              onClick={onAddClick}
              sx={{ marginLeft: 'auto' }}
            >
              {t('addRow')}
            </Button>
          )}
        </TableHeader>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableHeaderRow>
              {checkboxes && !disableActions && (
                <TableCell>
                  <Checkbox
                    checked={rows?.every((row) => row.checked)}
                    indeterminate={
                      rows?.some((row) => row.checked) &&
                      !rows?.every((row) => row.checked)
                    }
                    onChange={onCheckAll}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell key={col}>{col}</TableCell>
              ))}
              {hasActions && <TableCell>{t('actions')}</TableCell>}
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {rows &&
              rows.map((row) => (
                <ZebraStripedTableRow key={row.id}>
                  {checkboxes && (
                    <TableCell width={175}>
                      <Checkbox
                        checked={row.checked}
                        onChange={() => onCheckboxChange(row.id)}
                      />
                    </TableCell>
                  )}
                  {row.cells.map((cell, idx) => {
                    if (typeof cell === 'object' && cell !== null) {
                      return (
                        <TableCell key={`${cell.value}_${idx}`}>
                          {cell.value}
                        </TableCell>
                      );
                    }
                    return <TableCell key={`${cell}_${idx}`}>{cell}</TableCell>;
                  })}
                  {hasActions && (
                    <TableCell>
                      {onCopyClick && (
                        <Tooltip title={`${t('copy')}`} arrow>
                          <IconButton
                            size="small"
                            onClick={() => onCopyClick(row.id)}
                          >
                            <Icon type="copy" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onEditClick && (
                        <Tooltip title={`${t('edit')}`} arrow>
                          <IconButton
                            size="small"
                            onClick={() => onEditClick(row.id)}
                          >
                            <Icon type="pen" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDeleteClick && (
                        <Tooltip title={`${t('remove')}`} arrow>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteClick(row.id)}
                          >
                            <Icon type="trash-can" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </ZebraStripedTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showDeleteConfirmation && (
        <ConfirmationDialog
          handleAction={onDeleteConfirmation}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          type="warning"
          title={confirmationTitle}
        >
          <p>{confirmationBody}</p>
        </ConfirmationDialog>
      )}
      {addRowDialog}
    </Box>
  );
};

export default CustomTable;
